import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['button', 'menu']
  // autoPosition: JS positions the fixed menu (right-aligned) and follows/closes it on scroll.
  // Set to false to let the menu position itself via CSS (e.g. absolute/top-full anchored to the trigger).
  static values = { autoPosition: { type: Boolean, default: true } }

  connect() {
    this.boundClickOutside = this.handleClickOutside.bind(this)
    this.boundScroll = this.handleScroll.bind(this)
    document.addEventListener('click', this.boundClickOutside)
    window.addEventListener('scroll', this.boundScroll, true) // Use capture to catch all scroll events
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
    window.removeEventListener('scroll', this.boundScroll, true)
  }

  toggle(event) {
    event?.stopPropagation()
    const isHidden = this.menuTarget.classList.contains('hidden')

    // If we're about to show this dropdown, hide all other dropdowns first
    if (isHidden) {
      const otherDropdowns = document.querySelectorAll('[data-controller="dropdown"] [data-dropdown-target="menu"]')
      otherDropdowns.forEach(dropdown => {
        if (dropdown !== this.menuTarget && !dropdown.classList.contains('hidden')) {
          // Find the controller instance for this dropdown
          const controller = this.application.getControllerForElementAndIdentifier(
            dropdown.closest('[data-controller="dropdown"]'),
            'dropdown'
          )
          controller?.hide()
        }
      })

      // Position the menu relative to the button (unless the menu anchors itself via CSS).
      if (this.autoPositionValue) this.positionMenu()
    }

    this.buttonTarget.classList.toggle('active')
    this.menuTarget.classList.toggle('hidden')
  }

  positionMenu() {
    const buttonRect = this.buttonTarget.getBoundingClientRect()

    // Position the menu just below the button, right-aligned (assumes a ~w-40 menu).
    this.menuTarget.style.top = `${buttonRect.bottom + 4}px`
    this.menuTarget.style.left = `${buttonRect.right - 160}px`
  }

  hide() {
    this.buttonTarget.classList.remove('active')
    this.menuTarget.classList.add('hidden')
  }

  handleClickOutside(event) {
    if (!this.hasMenuTarget || !this.hasButtonTarget) return

    if (!this.element.contains(event.target) && !this.menuTarget.classList.contains('hidden')) {
      this.hide()
    }
  }

  handleScroll(event) {
    if (!this.hasMenuTarget || !this.hasButtonTarget) return
    if (this.menuTarget.classList.contains('hidden')) return
    // A CSS-anchored menu scrolls with its trigger and tucks away naturally — nothing to do.
    if (!this.autoPositionValue) return

    // Close instead of floating the menu when its trigger scrolls out of view or behind a sticky
    // header — otherwise the fixed-position menu hangs over the page while its anchor is hidden.
    if (this.buttonHidden()) {
      this.hide()
    } else {
      this.positionMenu()
    }
  }

  buttonHidden() {
    const rect = this.buttonTarget.getBoundingClientRect()
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return true

    // Occluded (e.g. by a sticky header) if something outside this dropdown sits at the button's center.
    const el = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
    return !(el && this.element.contains(el))
  }
}
